import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Trophy, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { API_URL } from '../utils/api';
import { SEO } from '../components/SEO';
import axios from 'axios';

function VerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [booking, setBooking] = useState(null);

  const hash = searchParams.get('hash');
  const id = searchParams.get('id');

  useEffect(() => {
    if (!hash || !id) {
      setStatus('invalid');
      return;
    }
    const verify = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/bookings/verify?hash=${hash}&id=${id}`);
        setBooking(res.data.booking);
        setStatus(res.data.valid ? 'valid' : 'invalid');
      } catch {
        setStatus('invalid');
      }
    };
    verify();
  }, [hash, id]);

  const formatSlot = (slot) => {
    const h = parseInt(slot.split(':')[0]);
    return `${h > 12 ? h - 12 : h === 0 ? 12 : h}${h >= 12 ? 'PM' : 'AM'}`;
  };

  const HAPPY_SET = new Set(['03:00','04:00','05:00','06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00']);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col">
      <SEO
        title="Verify Booking"
        description="Verify a Malda Turf booking by scanning the QR code on your receipt."
        path="/verify"
        noindex
      />
      <nav className="border-b border-[#1A1A1A] bg-[#0A0A0A]/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <Trophy className="h-7 w-7 text-[#16A34A]" />
            <span className="text-xl font-bold tracking-tight">Malda Turf</span>
          </div>
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-md w-full">
          {status === 'loading' && (
            <div className="bg-[#171717] border border-[#1A1A1A] rounded-2xl p-12 text-center">
              <Loader2 className="h-12 w-12 text-[#16A34A] animate-spin mx-auto mb-6" />
              <h2 className="text-xl font-bold mb-2">Verifying Booking...</h2>
              <p className="text-sm text-gray-500 font-body">Please wait</p>
            </div>
          )}

          {status === 'valid' && booking && (() => {
            const slots = booking.time_slots || [];
            const happyCount = slots.filter(s => HAPPY_SET.has(s)).length;
            const rushCount = slots.length - happyCount;
            return (
              <div className="bg-[#171717] border border-[#1A1A1A] rounded-2xl overflow-hidden">
                <div className="h-1 bg-[#16A34A]"></div>
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#16A34A] rounded-full flex items-center justify-center mx-auto mb-5">
                    <CheckCircle className="h-8 w-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold mb-1">Booking Verified</h2>
                  <p className="text-sm text-gray-500 font-body mb-6">This is a valid Malda Turf booking</p>

                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-5 text-left space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 font-body">Date</span>
                      <span className="font-semibold">{booking.booking_date}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 font-body">Customer</span>
                      <span>{booking.customer_name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 font-body">Slots</span>
                      <span className="text-right text-xs">
                        {happyCount > 0 && `Happy × ${happyCount}`}
                        {happyCount > 0 && rushCount > 0 && ' · '}
                        {rushCount > 0 && `Rush × ${rushCount}`}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 font-body">Time</span>
                      <span className="text-xs">{slots.map(formatSlot).join(', ')}</span>
                    </div>
                    <div className="h-[1px] bg-[#1A1A1A]"></div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 font-body text-sm">Amount</span>
                      <span className="text-xl font-bold text-[#16A34A]" style={{ fontFamily: 'Inter, sans-serif' }}>₹{booking.total_amount}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 font-body">Status</span>
                      <span className="text-[#16A34A] font-semibold uppercase text-xs">{booking.status}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {status === 'invalid' && (
            <div className="bg-[#171717] border border-[#1A1A1A] rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-red-500/20 border border-red-500/30 rounded-full flex items-center justify-center mx-auto mb-5">
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Invalid Booking</h2>
              <p className="text-sm text-gray-500 font-body mb-6">This QR code is invalid or the booking could not be found.</p>
              <button onClick={() => navigate('/')} className="btn-primary px-6 py-2.5 text-sm">Go to Homepage</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VerifyPage;
